import React from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Stack,
  useTheme
} from "@mui/material";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import PageTransition from "../components/PageTransition";

function TermsPage() {
  const theme = useTheme();

  return (
    <PageTransition>
      <Helmet>
        <title>Terms of Service – David Kooi</title>
        <link rel="canonical" href="https://davidkooi.com/terms/" />
        <meta name="description" content="Terms of service for davidkooi.com - usage guidelines and legal terms for the website and ChatDDK service." />
      </Helmet>
      
      <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.background.default }}>
        <Navigation />
      
      <Container component="main" id="main" maxWidth="md" sx={{ py: 6 }}>
        <Stack spacing={4}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, textAlign: "center" }}>
            Terms of Service
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            {/* Hard-coded on purpose: see PrivacyPage. */}
            Last updated: December 7, 2025
          </Typography>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              By accessing and using this website, you accept and agree to be bound by the terms and provision 
              of this agreement. If you do not agree to abide by the above, please do not use this service.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Use of Website
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              This website is intended for informational and educational purposes. You may use the website 
              for lawful purposes only. You agree not to use the website:
            </Typography>
            <Typography component="div" variant="body1" paragraph sx={{ lineHeight: 1.8, pl: 2 }}>
              • In any way that violates applicable laws or regulations<br />
              • To transmit harmful, offensive, or inappropriate content<br />
              • To attempt to gain unauthorized access to any portion of the website<br />
              • To interfere with the proper working of the website<br />
              • To use automated systems to access the website beyond normal browsing
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              ChatDDK Service
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              The ChatDDK AI assistant is provided for informational and entertainment purposes only. 
              The responses generated are based on available training data and should not be considered 
              as professional advice of any kind.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Usage of the ChatDDK service is subject to rate limits (currently 10 messages per day per IP address) 
              to ensure fair access for all users. We reserve the right to modify these limits as needed.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              ChatDDK does not have access to confidential information, and any responses claiming to contain 
              such information should be considered fictional or erroneous.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Content and Intellectual Property
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              The content on this website, including text, graphics, logos, and software, is the property 
              of David Kooi or its content suppliers and is protected by copyright and other intellectual 
              property laws.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              You may not reproduce, distribute, modify, or create derivative works of any content without 
              explicit written permission.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Disclaimers
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              The information on this website is provided on an "as is" basis. We make no warranties, 
              expressed or implied, regarding the accuracy, completeness, or reliability of the content.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We disclaim all liability for any damages arising from your use of this website or reliance 
              on its content, including but not limited to direct, indirect, incidental, or consequential damages.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Service Availability
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We strive to maintain continuous service availability but cannot guarantee uninterrupted access. 
              We reserve the right to suspend or terminate service at any time for maintenance, updates, 
              or other operational needs.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Modifications to Terms
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We reserve the right to modify these terms at any time. Changes will be effective immediately 
              upon posting to the website. Your continued use of the website constitutes acceptance of 
              any modifications.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Contact Information
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              If you have questions about these Terms of Service, please contact us at hello@skylarkcreations.com.
            </Typography>
          </Box>
        </Stack>
      </Container>

      <Footer />
    </Box>
  </PageTransition>
  );
}

export default TermsPage;
